import { AimOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Flex, InputNumber, Space, Typography } from 'antd';
import { DEFAULT_GPS_POINT } from '@/features/inventorization/position/gps-position-geo';
import type { GeoPoint } from '@/routers/inventorization/components/position/GoogleMarkerMapPicker';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface GpsPositionControlsProps {
  backLabel: string;
  currentPosition: GeoPoint | null;
  isLocating: boolean;
  onSkipGps: () => void;
  onBack: () => void;
  onSave: () => void;
  onSelectPin: (pin: GeoPoint) => void;
  onUseCurrentGps: () => void;
  saveLabel: string;
  selectedPin: GeoPoint | null;
  showSkipGps: boolean;
}

export function GpsPositionControls({
  backLabel,
  currentPosition,
  isLocating,
  onSkipGps,
  onBack,
  onSave,
  onSelectPin,
  onUseCurrentGps,
  saveLabel,
  selectedPin,
  showSkipGps,
}: GpsPositionControlsProps) {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text>{translateInventoryText('app.inventory.position.gps.latitude', 'Latitude')}</Typography.Text>
          <InputNumber
            onChange={value =>
              onSelectPin({
                lat: Number(value ?? selectedPin?.lat ?? DEFAULT_GPS_POINT.lat),
                lng: selectedPin?.lng ?? DEFAULT_GPS_POINT.lng,
              })
            }
            precision={6}
            step={0.000001}
            style={{ width: '100%' }}
            value={selectedPin?.lat ?? null}
          />
        </Space>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Typography.Text>
            {translateInventoryText('app.inventory.position.gps.longitude', 'Longitude')}
          </Typography.Text>
          <InputNumber
            onChange={value =>
              onSelectPin({
                lat: selectedPin?.lat ?? DEFAULT_GPS_POINT.lat,
                lng: Number(value ?? selectedPin?.lng ?? DEFAULT_GPS_POINT.lng),
              })
            }
            precision={6}
            step={0.000001}
            style={{ width: '100%' }}
            value={selectedPin?.lng ?? null}
          />
        </Space>
      </div>

      <Flex gap="small" wrap>
        <Button disabled={!currentPosition} icon={<AimOutlined />} loading={isLocating} onClick={onUseCurrentGps}>
          {translateInventoryText('app.inventory.position.actions.useCurrentGps', 'Use current GPS')}
        </Button>
      </Flex>

      <Flex gap="small" justify="space-between" wrap>
        <Button onClick={onBack} size="large">
          {backLabel}
        </Button>
        <Flex gap="small" wrap>
          {showSkipGps ? (
            <Button icon={<StopOutlined />} onClick={onSkipGps} size="large">
              {translateInventoryText('app.inventory.position.actions.skipGps', 'Skip GPS')}
            </Button>
          ) : null}
          <Button disabled={!selectedPin} onClick={onSave} size="large" type="primary">
            {saveLabel}
          </Button>
        </Flex>
      </Flex>
    </Space>
  );
}
